'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import { getCorporateEventColor, getCorporateEventDisplayLabel } from '@/libs/corporate-event-chart';

type LiquidityPoint = {
  date: string;
  cotistas: number;
  cotas: number | null;
  liquidezDiaria30d: number | null;
};

type TickerLiquidityTabClientProps = {
  tickerLabel: string;
  points: LiquidityPoint[];
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
};

type RangeId = '1A' | '3A' | '5A' | '10A' | 'TUDO';

const DAY_MS = 24 * 60 * 60 * 1000;
const PLOT_VERTICAL_PADDING_RATIO = 0.08;
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});

const RANGE_CONFIG: Array<{ id: RangeId; label: string; days: number | null }> = [
  { id: '1A', label: '1A', days: 365 },
  { id: '3A', label: '3A', days: 365 * 3 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: '10A', label: '10A', days: 365 * 10 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const mapValueToY = (value: number, min: number, max: number, chartTop: number, chartHeight: number) => {
  const innerPadding = chartHeight * PLOT_VERTICAL_PADDING_RATIO;
  const usableHeight = Math.max(1, chartHeight - innerPadding * 2);
  if (min === max) return chartTop + innerPadding + usableHeight / 2;
  const ratio = (value - min) / (max - min);
  return chartTop + innerPadding + usableHeight - ratio * usableHeight;
};

export function TickerLiquidityTabClient({ tickerLabel, points, corporateEvents }: TickerLiquidityTabClientProps) {
  const colors = useAppColors();
  const rangeColors = colors.actions.range;
  const whiteColor = colors.semantic.white;
  const liquidityColor = colors.modules.liquidity.liquidity;
  const cotistasColor = colors.modules.liquidity.cotistas;
  const cotasColor = colors.modules.liquidity.cotas;
  const [range, setRange] = useState<RangeId>('5A');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [showCotistas, setShowCotistas] = useState(true);
  const [showCotas, setShowCotas] = useState(true);
  const [showLiquidez, setShowLiquidez] = useState(true);

  const ordered = useMemo(
    () => [...points].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [points],
  );
  const availableWindowDays = useMemo(() => {
    if (ordered.length < 2) return 0;
    const start = new Date(ordered[0].date).getTime();
    const end = new Date(ordered[ordered.length - 1].date).getTime();
    return Math.max(0, Math.floor((end - start) / DAY_MS));
  }, [ordered]);

  const filtered = useMemo(() => {
    if (ordered.length === 0) return ordered;
    const selected = RANGE_CONFIG.find((item) => item.id === range);
    if (!selected || selected.days === null) return ordered;
    const lastTs = new Date(ordered[ordered.length - 1].date).getTime();
    const threshold = lastTs - selected.days * DAY_MS;
    const next = ordered.filter((item) => new Date(item.date).getTime() >= threshold);
    return next.length > 1 ? next : ordered;
  }, [ordered, range]);
  const filteredCorporateEvents = useMemo(() => {
    if (filtered.length === 0) return [];
    const startDate = filtered[0].date.slice(0, 10);
    const endDate = filtered[filtered.length - 1].date.slice(0, 10);
    return corporateEvents
      .filter((event) => event.date >= startDate && event.date <= endDate)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [corporateEvents, filtered]);

  const latest = filtered[filtered.length - 1] ?? ordered[ordered.length - 1];
  const latestCotas =
    [...filtered].reverse().find((item) => item.cotas !== null)?.cotas ??
    [...ordered].reverse().find((item) => item.cotas !== null)?.cotas ??
    null;
  const latestLiquidezDiaria30d =
    [...filtered].reverse().find((item) => item.liquidezDiaria30d !== null)?.liquidezDiaria30d ??
    [...ordered].reverse().find((item) => item.liquidezDiaria30d !== null)?.liquidezDiaria30d ??
    null;

  const width = 1120;
  const height = 300;
  const chartLeft = 96;
  const chartRight = width - 160;
  const chartTop = 20;
  const chartBottom = height - 42;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const cotistasValues = filtered.map((point) => point.cotistas);
  const cotistasMinData = Math.min(...cotistasValues);
  const cotistasMaxData = Math.max(...cotistasValues);
  const cotistasRangeData = cotistasMaxData - cotistasMinData;
  const cotistasPadding =
    cotistasRangeData === 0 ? Math.max(Math.abs(cotistasMaxData) * 0.08, 1) : cotistasRangeData * 0.08;
  const cotistasMin = cotistasMinData - cotistasPadding;
  const cotistasMax = cotistasMaxData + cotistasPadding;

  const cotasValues = filtered.map((point) => point.cotas).filter((value): value is number => value !== null);
  const cotasMinData = cotasValues.length > 0 ? Math.min(...cotasValues) : 0;
  const cotasMaxData = cotasValues.length > 0 ? Math.max(...cotasValues) : 1;
  const cotasRangeData = cotasMaxData - cotasMinData;
  const cotasPadding = cotasRangeData === 0 ? Math.max(Math.abs(cotasMaxData) * 0.08, 1) : cotasRangeData * 0.08;
  const cotasMin = cotasMinData - cotasPadding;
  const cotasMax = cotasMaxData + cotasPadding;
  const liquidezValues = filtered
    .map((point) => point.liquidezDiaria30d)
    .filter((value): value is number => value !== null);
  const liquidezMinData = liquidezValues.length > 0 ? Math.min(...liquidezValues) : 0;
  const liquidezMaxData = liquidezValues.length > 0 ? Math.max(...liquidezValues) : 1;
  const liquidezRangeData = liquidezMaxData - liquidezMinData;
  const liquidezPadding =
    liquidezRangeData === 0 ? Math.max(Math.abs(liquidezMaxData) * 0.08, 1) : liquidezRangeData * 0.08;
  const liquidezMin = liquidezMinData - liquidezPadding;
  const liquidezMax = liquidezMaxData + liquidezPadding;

  const cotistasPoints = filtered.map((point, index) => {
    const x =
      filtered.length === 1 ? chartLeft + chartWidth / 2 : chartLeft + (index / (filtered.length - 1)) * chartWidth;
    const y = mapValueToY(point.cotistas, cotistasMin, cotistasMax, chartTop, chartHeight);
    return { x, y };
  });
  const cotistasPolyline = showCotistas ? cotistasPoints.map((point) => `${point.x},${point.y}`).join(' ') : '';
  const cotasPoints = filtered.map((point, index) => {
    if (point.cotas === null) return null;
    const x =
      filtered.length === 1 ? chartLeft + chartWidth / 2 : chartLeft + (index / (filtered.length - 1)) * chartWidth;
    const y = mapValueToY(point.cotas, cotasMin, cotasMax, chartTop, chartHeight);
    return { x, y };
  });
  const cotasPolyline = showCotas
    ? cotasPoints
        .filter((point): point is { x: number; y: number } => point !== null)
        .map((point) => `${point.x},${point.y}`)
        .join(' ')
    : '';
  const liquidezPoints = filtered.map((point, index) => {
    if (point.liquidezDiaria30d === null) return null;
    const x =
      filtered.length === 1 ? chartLeft + chartWidth / 2 : chartLeft + (index / (filtered.length - 1)) * chartWidth;
    const y = mapValueToY(point.liquidezDiaria30d, liquidezMin, liquidezMax, chartTop, chartHeight);
    return { x, y };
  });
  const liquidezPolyline = showLiquidez
    ? liquidezPoints
        .filter((point): point is { x: number; y: number } => point !== null)
        .map((point) => `${point.x},${point.y}`)
        .join(' ')
    : '';
  const hoveredPoint =
    hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < filtered.length
      ? { ...cotistasPoints[hoveredIndex], data: filtered[hoveredIndex] }
      : null;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
        >
          <Typography variant='h6' fontWeight={700}>
            Evolução de liquidez de {tickerLabel}
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {RANGE_CONFIG.map((item) => (
              <Button
                key={item.id}
                size='small'
                variant={range === item.id ? 'contained' : 'outlined'}
                disabled={Boolean(item.days && item.days > availableWindowDays)}
                onClick={() => setRange(item.id)}
                sx={
                  range === item.id
                    ? {
                        bgcolor: rangeColors.selectedBg,
                        borderColor: rangeColors.selectedBorder,
                        color: rangeColors.selectedText,
                        '&:hover': {
                          bgcolor: rangeColors.selectedHoverBg,
                          borderColor: rangeColors.selectedHoverBorder,
                        },
                      }
                    : {
                        color: rangeColors.unselectedText,
                        borderColor: rangeColors.unselectedBorder,
                        '&:hover': {
                          color: rangeColors.unselectedHoverText,
                          borderColor: rangeColors.unselectedHoverBorder,
                          backgroundColor: rangeColors.unselectedHoverBg,
                        },
                      }
                }
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 2 }}>
          <Metric label='Cotistas atuais' value={NUMBER_FORMATTER.format(latest.cotistas)} />
          <Metric
            label='Cotas emitidas atuais'
            value={latestCotas === null ? '-' : NUMBER_FORMATTER.format(latestCotas)}
          />
          <Metric
            label='Liquidez diária 30d'
            value={latestLiquidezDiaria30d === null ? '-' : BRL_FORMATTER.format(latestLiquidezDiaria30d)}
          />
          <Metric label='Competência' value={DATE_FORMATTER.format(new Date(latest.date))} />
        </Stack>
        <Stack direction='row' spacing={1} sx={{ mt: 1 }} flexWrap='wrap' useFlexGap>
          <SeriesToggle
            active={showCotistas}
            color={liquidityColor}
            whiteColor={whiteColor}
            label='Nº de cotistas'
            onClick={() => setShowCotistas((prev) => !prev)}
          />
          <SeriesToggle
            active={showCotas}
            color={cotistasColor}
            whiteColor={whiteColor}
            label='Nº de cotas'
            onClick={() => setShowCotas((prev) => !prev)}
          />
          <SeriesToggle
            active={showLiquidez}
            color={cotasColor}
            whiteColor={whiteColor}
            label='Liquidez diária (30d)'
            onClick={() => setShowLiquidez((prev) => !prev)}
          />
        </Stack>

        <Box sx={{ width: '100%', mt: 1.5 }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width='100%'
            role='img'
            onMouseLeave={() => setHoveredIndex(null)}
            onMouseMove={(event) => {
              if (filtered.length === 0) return;
              const rect = event.currentTarget.getBoundingClientRect();
              if (rect.width <= 0 || chartWidth <= 0) return;
              const relativeX = ((event.clientX - rect.left) / rect.width) * width;
              const clampedX = Math.max(chartLeft, Math.min(chartRight, relativeX));
              const ratio = (clampedX - chartLeft) / chartWidth;
              const index = Math.round(ratio * (filtered.length - 1));
              const safeIndex = Math.max(0, Math.min(filtered.length - 1, index));
              setHoveredIndex(safeIndex);
            }}
          >
            <rect
              x={chartLeft}
              y={chartTop}
              width={chartWidth}
              height={chartHeight}
              fill='transparent'
              stroke='hsl(var(--border))'
            />
            {cotistasPolyline ? (
              <polyline points={cotistasPolyline} fill='none' stroke={liquidityColor} strokeWidth='2' />
            ) : null}
            {cotasPolyline ? (
              <polyline
                points={cotasPolyline}
                fill='none'
                stroke={cotistasColor}
                strokeWidth='2'
                strokeDasharray='4 3'
              />
            ) : null}
            {liquidezPolyline ? (
              <polyline
                points={liquidezPolyline}
                fill='none'
                stroke={cotasColor}
                strokeWidth='2'
                strokeDasharray='2 3'
              />
            ) : null}
            {filteredCorporateEvents.map((event, index) => {
              const eventTs = new Date(`${event.date}T00:00:00Z`).getTime();
              let closestIndex = 0;
              let closestDistance = Number.POSITIVE_INFINITY;
              for (let pointIndex = 0; pointIndex < filtered.length; pointIndex += 1) {
                const pointTs = new Date(filtered[pointIndex].date).getTime();
                const distance = Math.abs(pointTs - eventTs);
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestIndex = pointIndex;
                }
              }
              const x =
                filtered.length === 1
                  ? chartLeft + chartWidth / 2
                  : chartLeft + (closestIndex / (filtered.length - 1)) * chartWidth;
              const color = getCorporateEventColor(event);
              return (
                <g key={`${event.date}-${event.eventType}-${index}`}>
                  <line
                    x1={x}
                    y1={chartTop}
                    x2={x}
                    y2={chartBottom}
                    stroke={color}
                    strokeWidth='1.1'
                    strokeDasharray='3 3'
                    opacity='0.72'
                  />
                  <rect x={x - 2} y={chartTop + 2} width='4' height='8' rx='1' fill={color}>
                    <title>
                      {`${getCorporateEventDisplayLabel(event)} em ${DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))}`}
                    </title>
                  </rect>
                </g>
              );
            })}
            {showCotistas
              ? cotistasPoints.map((point, idx) => (
                  <circle key={idx} cx={point.x} cy={point.y} r='2.2' fill={liquidityColor} />
                ))
              : null}

            {hoveredPoint && (showCotistas || showCotas || showLiquidez) ? (
              <>
                <line
                  x1={hoveredPoint.x}
                  y1={chartTop}
                  x2={hoveredPoint.x}
                  y2={chartBottom}
                  stroke='hsl(var(--muted-foreground) / 0.6)'
                  strokeDasharray='4 4'
                />
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r='4'
                  fill={liquidityColor}
                  stroke={whiteColor}
                  strokeWidth='2'
                />
                <g
                  transform={`translate(${Math.min(
                    chartRight - 210,
                    Math.max(chartLeft + 8, hoveredPoint.x + 10),
                  )}, ${chartTop + 8})`}
                >
                  <rect width='220' height='76' rx='8' fill='hsl(var(--card))' stroke='hsl(var(--border))' />
                  <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                    {DATE_FORMATTER.format(new Date(hoveredPoint.data.date))}
                  </text>
                  {showCotistas ? (
                    <text x='10' y='35' fontSize='13' fontWeight='700' fill='hsl(var(--foreground))'>
                      {`Cotistas: ${NUMBER_FORMATTER.format(hoveredPoint.data.cotistas)}`}
                    </text>
                  ) : null}
                  {showCotas ? (
                    <text x='10' y={showCotistas ? '51' : '35'} fontSize='12' fill={cotistasColor}>
                      {`Cotas: ${
                        hoveredPoint.data.cotas === null ? '-' : NUMBER_FORMATTER.format(hoveredPoint.data.cotas)
                      }`}
                    </text>
                  ) : null}
                  {showLiquidez ? (
                    <text
                      x='10'
                      y={showCotistas ? (showCotas ? '67' : '51') : showCotas ? '51' : '35'}
                      fontSize='12'
                      fill={cotasColor}
                    >
                      {`Liq. 30d: ${
                        hoveredPoint.data.liquidezDiaria30d === null
                          ? '-'
                          : BRL_FORMATTER.format(hoveredPoint.data.liquidezDiaria30d)
                      }`}
                    </text>
                  ) : null}
                </g>
              </>
            ) : null}

            <text x={chartLeft - 8} y={chartTop + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
              {NUMBER_FORMATTER.format(cotistasMax)}
            </text>
            <text
              x={chartLeft - 8}
              y={chartBottom + 4}
              textAnchor='end'
              fontSize='12'
              fill='hsl(var(--muted-foreground))'
            >
              {NUMBER_FORMATTER.format(cotistasMin)}
            </text>
            {showCotas && cotasValues.length > 0 ? (
              <>
                <text x={chartRight + 8} y={chartTop + 4} textAnchor='start' fontSize='12' fill={cotistasColor}>
                  {NUMBER_FORMATTER.format(cotasMax)}
                </text>
                <text x={chartRight + 8} y={chartBottom + 4} textAnchor='start' fontSize='12' fill={cotistasColor}>
                  {NUMBER_FORMATTER.format(cotasMin)}
                </text>
              </>
            ) : null}
            {showLiquidez && liquidezValues.length > 0 ? (
              <>
                <text x={chartRight + 76} y={chartTop + 4} textAnchor='start' fontSize='12' fill={cotasColor}>
                  {BRL_FORMATTER.format(liquidezMax)}
                </text>
                <text x={chartRight + 76} y={chartBottom + 4} textAnchor='start' fontSize='12' fill={cotasColor}>
                  {BRL_FORMATTER.format(liquidezMin)}
                </text>
              </>
            ) : null}
            <text x={chartLeft} y={height - 16} fontSize='12' fill='hsl(var(--muted-foreground))'>
              {DATE_FORMATTER.format(new Date(filtered[0].date))}
            </text>
            <text x={chartRight} y={height - 16} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
              {DATE_FORMATTER.format(new Date(filtered[filtered.length - 1].date))}
            </text>
          </svg>
        </Box>
        {filteredCorporateEvents.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
              Eventos corporativos no período
            </Typography>
            <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap' sx={{ mt: 0.5 }}>
              {filteredCorporateEvents.slice(0, 8).map((event, index) => (
                <Stack
                  key={`${event.date}-${event.eventType}-${index}`}
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 1,
                    backgroundColor: 'hsl(var(--muted) / 0.35)',
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getCorporateEventColor(event),
                    }}
                  />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))} ·{' '}
                    {getCorporateEventDisplayLabel(event)}
                  </Typography>
                </Stack>
              ))}
              {filteredCorporateEvents.length > 8 ? (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  +{filteredCorporateEvents.length - 8} eventos
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Paper>
    </Stack>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='h6' fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  );
}

function SeriesToggle({
  active,
  color,
  whiteColor,
  label,
  onClick,
}: {
  active: boolean;
  color: string;
  whiteColor: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      variant={active ? 'contained' : 'outlined'}
      onClick={onClick}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        gap: 0.75,
        ...(active
          ? {
              bgcolor: color,
              borderColor: color,
              '&:hover': { bgcolor: color, opacity: 0.9 },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: active ? whiteColor : color,
        }}
      />
      {label}
    </Button>
  );
}
